import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Link as ChakraLink,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import Logo from '../components/Logo';
import { api } from '../services/api';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  
  const validateForm = () => {
    let isValid = true;
    
    // メールバリデーション
    if (!email) {
      setEmailError('メールアドレスは必須です');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('有効なメールアドレスを入力してください');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // パスワードバリデーション
    if (!password) {
      setPasswordError('パスワードは必須です');
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('パスワードは8文字以上である必要があります');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    // 確認用パスワードバリデーション
    if (password !== confirmPassword) {
      setConfirmPasswordError('パスワードが一致しません');
      isValid = false;
    } else {
      setConfirmPasswordError('');
    }
    
    return isValid;
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setGeneralError('');
    
    try {
      await api.post('/auth/register', {
        email,
        password,
        role: 'user'
      });
      
      // 登録成功
      setSuccess(true);
      
      // 成功後、3秒後にログインページにリダイレクト
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Registration error:', error);
      
      // エラーメッセージの設定
      if (error.response?.data?.detail) {
        setGeneralError(error.response.data.detail);
      } else {
        setGeneralError('アカウント登録中にエラーが発生しました。後でもう一度お試しください。');
      }
      
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', md: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6" align="center">
          <Logo width="120px" />
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading size={{ base: 'md', md: 'lg' }}>アカウント作成</Heading>
            <Text color="fg.muted">GenX AI-OCRを始めよう</Text>
          </Stack>
        </Stack>
        
        <Box
          py={{ base: '8', md: '8' }}
          px={{ base: '4', md: '10' }}
          bg={useColorModeValue('white', 'gray.800')}
          boxShadow={{ base: 'none', md: 'xl' }}
          borderRadius={{ base: 'none', md: 'xl' }}
        >
          {success ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              アカウントが作成されました。ログインページにリダイレクトします...
            </Alert>
          ) : (
            <form onSubmit={handleRegister}>
              <Stack spacing="6">
                {/* エラーメッセージ */}
                {generalError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {generalError}
                  </Alert>
                )}
                
                <Stack spacing="5">
                  {/* メールアドレス入力 */}
                  <FormControl isInvalid={!!emailError}>
                    <FormLabel htmlFor="email">メールアドレス</FormLabel>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    {emailError && <FormErrorMessage>{emailError}</FormErrorMessage>}
                  </FormControl>
                  
                  {/* パスワード入力 */}
                  <FormControl isInvalid={!!passwordError}>
                    <FormLabel htmlFor="password">パスワード</FormLabel>
                    <InputGroup>
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                          icon={showPassword ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                  </FormControl>
                  
                  {/* 確認用パスワード入力 */}
                  <FormControl isInvalid={!!confirmPasswordError}>
                    <FormLabel htmlFor="confirmPassword">パスワード（確認）</FormLabel>
                    <InputGroup>
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                          icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {confirmPasswordError && <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>}
                  </FormControl>
                </Stack>
                
                {/* 登録ボタン */}
                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  fontSize="md"
                  isLoading={isSubmitting}
                  loadingText="登録中..."
                >
                  アカウント作成
                </Button>
                
                {/* ログインリンク */}
                <Stack spacing="1" justify="center" direction="row">
                  <Text>すでにアカウントをお持ちの方は</Text>
                  <ChakraLink as={RouterLink} to="/login" color="brand.500">
                    ログイン
                  </ChakraLink>
                </Stack>
              </Stack>
            </form>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default Register;